import { EventType } from "@/analytics/analytics-events";
import { SubmitHbToSxgMigrationFeedbackProperties } from "@/analytics/hb-to-sxg-migration-events-properties";
import { computeDeepLink } from "@/components/common/deep-link/deep-link-utils";
import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import { useSubmitExperienceFeedback } from "@/hooks/use-submit-experience-feedback";
import { selectActiveElementId } from "@/store/selections-selectors";
import { useAppStore } from "@/store/store-hooks";
import { generateEntryPageRedirectUrl } from "@/utils/redirects";
import { ExperienceFeedbackDialog } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useCallback, useState, type JSX } from "react";

type SwitchToPreviousUiDialogProps = {
  /** Whether the dialog is shown */
  open: boolean;

  /** Called when the user cancels the switch and stays in the viewer */
  onClose(): void;
};

/**
 * @returns the dialog collecting the feedback of a user switching back to the HoloBuilder WebEditor,
 */
export function SwitchToPreviousUiDialog({
  open,
  onClose,
}: SwitchToPreviousUiDialogProps): JSX.Element {
  const submitExperienceFeedback = useSubmitExperienceFeedback();
  const store = useAppStore();
  const viewContext = useViewRuntimeContext();
  const [isSwitching, setIsSwitching] = useState(false);

  const onClosePreviousUiDialog = useCallback(() => {
    Analytics.track(EventType.cancelHbToSxgMigrationFeedback);
    onClose();
  }, [onClose]);

  const submitAndSwitch = useCallback(
    async (reasons: string[], feedback: string) => {
      Analytics.track<SubmitHbToSxgMigrationFeedbackProperties>(
        EventType.submitHbToSxgMigrationFeedback,
        { reasons, feedback },
      );
      setIsSwitching(true);

      const isSubmitted = await submitExperienceFeedback({ reasons, feedback });
      if (!isSubmitted) {
        setIsSwitching(false);
        return;
      }

      // Only compute the Viewer deep link if there is an active element.
      // Otherwise, simply use the current page URL
      const state = store.getState();
      const viewerUrl = selectActiveElementId(state)
        ? computeDeepLink(state, viewContext)
        : new URL(window.location.pathname, window.location.origin);

      // Redirect to the EntryPage redirect route, that route will take care of translating the
      // Viewer deep link into its corresponding WebEditor deep link and redirect to it
      window.location.href = generateEntryPageRedirectUrl(viewerUrl).toString();
    },
    [submitExperienceFeedback, store, viewContext],
  );

  return (
    <ExperienceFeedbackDialog
      open={open}
      onClose={onClosePreviousUiDialog}
      onSubmitFeedback={submitAndSwitch}
      showSpinner={isSwitching}
    />
  );
}
